import { html, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { FloatField } from './float_field';

class FloatDropdown extends FloatField {
  static get properties() {
    return {
      options: { type: Array, attribute: false },
      type: { type: String, attribute: 'type' },
      open: { type: Boolean, state: true },
      selectedOption: { type: Object, state: true },
      activeDescendantIndex: { type: Number, state: true },
    };
  }

  constructor() {
    super();
    this.options = [];
    this.type = 'text';
    this.open = false;
    this.selectedOption = null;
    this.isPositioned = false;
    this.activeDescendantIndex = -1;

    this.validators = [
      {
        key: 'valueMissing',
        isValid: () => true,
        message: '',
      },
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.handleDefaultValue();
    this.handleClickOutside();

    let resizeTimeout;
    this.handleReposition = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (this.open && this.isConnected) {
          this.positionDropdown();
        }
      }, 16); // ~60fps debounce
    };

    // Handle mobile-specific viewport changes
    this.handleVisualViewportChange = () => {
      if (window.visualViewport && this.open) {
        this.handleReposition();
      }
    };

    window.addEventListener('resize', this.handleReposition, { passive: true });
    window.addEventListener('orientationchange', this.handleReposition, {
      passive: true,
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        this.handleVisualViewportChange,
        { passive: true }
      );
    }

    this.resizeObserver = new ResizeObserver(() => {
      if (this.open && !this.isPositioning) {
        this.handleReposition();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.handleReposition);
    window.removeEventListener('orientationchange', this.handleReposition);

    if (window.visualViewport && this.handleVisualViewportChange) {
      window.visualViewport.removeEventListener(
        'resize',
        this.handleVisualViewportChange
      );
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    const dropdown = this.renderRoot?.querySelector('.dropdown_list');
    if (dropdown && dropdown.open) {
      if (this.handleBackdropClick) {
        dropdown.removeEventListener('click', this.handleBackdropClick);
        this.handleBackdropClick = null;
      }
      if (this.handleDialogCancel) {
        dropdown.removeEventListener('cancel', this.handleDialogCancel);
        this.handleDialogCancel = null;
      }
      dropdown.close();
    }

    if (this.boundClickOutside) {
      document.removeEventListener('click', this.boundClickOutside);
    }
  }

  async calculatePosition(dropdown) {
    if (this.isPositioning) return {};
    this.isPositioning = true;

    const trigger = this.renderRoot.querySelector('.dropdown_trigger_button');
    const parent = this.closest('[dropdown-parent]') || this.parentElement;

    if (!trigger || !dropdown || !parent) {
      this.isPositioning = false;
      return {};
    }

    const triggerRect = trigger.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    if (
      (triggerRect.width === 0 && triggerRect.height === 0) ||
      (parentRect.width === 0 && parentRect.height === 0)
    ) {
      this.isPositioning = false;
      await new Promise((resolve) => requestAnimationFrame(resolve));
      return this.calculatePosition(dropdown);
    }

    const viewport = {
      width: Math.max(document.documentElement.clientWidth, window.innerWidth),
      height: Math.max(
        document.documentElement.clientHeight,
        window.innerHeight
      ),
    };

    const offsetParent = dropdown.offsetParent || document.body;
    const computedStyle = getComputedStyle(offsetParent);
    const hasTransform =
      computedStyle.transform !== 'none' ||
      computedStyle.perspective !== 'none' ||
      computedStyle.filter !== 'none';

    if (this.type === 'emoji') {
      await this.waitForEmojiContent(dropdown);
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    const dropdownRect = dropdown.getBoundingClientRect();
    const dropdownHeight = Math.max(dropdownRect.height, 300);

    const gap = 16;
    const safeMargin = 16;

    const positions = [
      {
        top: triggerRect.bottom + gap,
        left: Math.max(
          safeMargin,
          Math.min(
            parentRect.left,
            viewport.width - parentRect.width - safeMargin
          )
        ),
        side: 'below',
      },
      {
        top: triggerRect.top - dropdownHeight - gap,
        left: Math.max(
          safeMargin,
          Math.min(
            parentRect.left,
            viewport.width - parentRect.width - safeMargin
          )
        ),
        side: 'above',
      },
      {
        top: Math.max(
          safeMargin,
          Math.min(
            triggerRect.top,
            viewport.height - dropdownHeight - safeMargin
          )
        ),
        left: Math.max(
          safeMargin,
          Math.min(
            parentRect.left,
            viewport.width - parentRect.width - safeMargin
          )
        ),
        side: 'beside',
      },
    ];

    const validPosition =
      positions.find(
        (pos) =>
          pos.top >= safeMargin &&
          pos.top + dropdownHeight <= viewport.height - safeMargin &&
          pos.left >= safeMargin &&
          pos.left + parentRect.width <= viewport.width - safeMargin
      ) || positions[2]; // fallback to beside

    this.isPositioning = false;

    return {
      position: hasTransform ? 'absolute' : 'fixed',
      top: `${validPosition.top}px`,
      left: `${validPosition.left}px`,
      width: `${Math.min(
        parentRect.width,
        viewport.width - validPosition.left - safeMargin
      )}px`,
      maxHeight: `${Math.min(
        dropdownHeight,
        viewport.height - validPosition.top - safeMargin
      )}px`,
      margin: '0',
      overflow: 'auto',
    };
  }

  async positionDropdown() {
    const dropdown = this.renderRoot.querySelector('.dropdown_list');
    if (!dropdown) return;

    const styles = await this.calculatePosition(dropdown);
    Object.assign(dropdown.style, styles);
  }

  async waitForEmojiContent(dropdown) {
    const maxWait = 100;
    const startTime = Date.now();

    while (Date.now() - startTime < maxWait) {
      const emojiItems = dropdown.querySelectorAll(
        '.dropdown_option_button, .emoji-item, [data-emoji]'
      );
      if (emojiItems.length > 0) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
  }

  handleDefaultValue() {
    if (this.value && this.options.length > 0) {
      this.selectedOption =
        this.options.find(
          (option) =>
            option.value === this.value || option.display === this.value
        ) || this.getDefaultOption();
    } else if (this.defaultValue && this.options.length > 0) {
      this.selectedOption =
        this.options.find(
          (option) =>
            option.value === this.defaultValue ||
            option.display === this.defaultValue
        ) || this.getDefaultOption();
      this.value = this.selectedOption.value;
    } else if (this.options.length > 0) {
      this.selectedOption = this.getDefaultOption();
      this.value = this.selectedOption.value;
    }
  }

  getDefaultOption() {
    return (
      this.options.find((option) => option.default === true) || this.options[0]
    );
  }

  isMobileDevice() {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) ||
      'ontouchstart' in window ||
      window.innerWidth <= 768
    );
  }

  firstUpdated() {
    this.handleDefaultValue();

    const dropdown = this.renderRoot.querySelector('.dropdown_list');
    if (dropdown && this.resizeObserver) {
      this.resizeObserver.observe(dropdown);
    }
  }

  updated(changedProperties) {
    super.updated(changedProperties);

    if (
      changedProperties.has('value') ||
      changedProperties.has('options') ||
      changedProperties.has('defaultValue')
    ) {
      this.handleDefaultValue();
    }
  }

  setupDropdownHandlers(dropdown) {
    dropdown.dataset.dropdownId = this.id || 'dropdown';

    this.handleBackdropClick = (e) => {
      if (e.target === dropdown) {
        this.handleClose();
      }
    };
    dropdown.addEventListener('click', this.handleBackdropClick);

    this.handleDialogCancel = (e) => {
      e.preventDefault();
      this.handleClose();
    };
    dropdown.addEventListener('cancel', this.handleDialogCancel);
  }

  async handleOpen() {
    this.open = true;
    this.isPositioned = false;
    // Set initial activeDescendantIndex to currently selected option or first option
    this.activeDescendantIndex = this.selectedOption
      ? this.options.findIndex((opt) => opt.value === this.selectedOption.value)
      : 0;

    await this.updateComplete;

    const dropdown = this.renderRoot.querySelector('.dropdown_list');
    if (dropdown) {
      // Setup handlers first
      this.setupDropdownHandlers(dropdown);

      dropdown.style.visibility = 'hidden';
      dropdown.style.display = 'block';
      dropdown.style.opacity = '0';

      const styles = await this.calculatePosition(dropdown);
      Object.assign(dropdown.style, styles);

      dropdown.getBoundingClientRect();

      dropdown.showModal();
      dropdown.style.visibility = 'visible';
      dropdown.style.opacity = '1';

      this.isPositioned = true;
    }

    // Focus first option and update aria-activedescendant
    const firstOption = this.renderRoot.querySelector(
      '.dropdown_option_button'
    );
    if (firstOption) {
      firstOption.focus();
      setTimeout(() => this.updateActiveDescendantFromFocus(), 0);
    }
  }

  handleClose() {
    this.open = false;
    this.isPositioned = false;
    this.activeDescendantIndex = -1;

    const dropdown = this.renderRoot.querySelector('.dropdown_list');
    if (dropdown && dropdown.open) {
      // Remove event handlers
      if (this.handleBackdropClick) {
        dropdown.removeEventListener('click', this.handleBackdropClick);
        this.handleBackdropClick = null;
      }
      if (this.handleDialogCancel) {
        dropdown.removeEventListener('cancel', this.handleDialogCancel);
        this.handleDialogCancel = null;
      }

      // Reset visibility styles before closing
      dropdown.style.visibility = 'hidden';
      dropdown.style.opacity = '0';
      dropdown.style.display = 'none';

      dropdown.close();
    }

    this.renderRoot.querySelector('.dropdown_trigger_button')?.focus();
  }

  handleClickOutside() {
    if (this.boundClickOutside) {
      document.removeEventListener('click', this.boundClickOutside);
    }

    this.boundClickOutside = (e) => {
      if (this.open) {
        const dropdown = this.renderRoot.querySelector('.dropdown_list');
        const clickedInsideDropdown = dropdown && dropdown.contains(e.target);
        const clickedInsideComponent = this.contains(e.target);

        if (!clickedInsideDropdown && !clickedInsideComponent) {
          this.handleClose();
        }
      }
    };

    document.addEventListener('click', this.boundClickOutside);
  }

  handleSelectOption(option) {
    this.selectedOption = option;
    this.value = option.value;
    this.hasInteracted = true;
    this.handleClose();
    this.emitEvent('change', { detail: option.value });
  }

  updateActiveDescendantFromFocus() {
    const focusEls = this.renderRoot.querySelectorAll(
      '.dropdown_option_button'
    );
    const currentIndex = Array.from(focusEls).indexOf(document.activeElement);
    if (currentIndex >= 0) {
      this.activeDescendantIndex = currentIndex;
      this.requestUpdate();
    }
  }

  handleKeyDown(e) {
    if (!this.open) return;

    const focusEls = this.renderRoot.querySelectorAll(
      '.dropdown_option_button'
    );
    const firstFocusableEl = focusEls[0];
    const lastFocusableEl = focusEls[focusEls.length - 1];
    const currentIndex = Array.from(focusEls).indexOf(document.activeElement);

    switch (e.code) {
      case 'Tab':
        // Restore original Tab behavior with ARIA tracking
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableEl) {
            lastFocusableEl.focus();
            e.preventDefault();
          }
        } else if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
        // Update activeDescendantIndex after Tab navigation
        setTimeout(() => this.updateActiveDescendantFromFocus(), 0);
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (this.type === 'emoji') {
          // For emoji grid layout, handle column-based navigation
          const nextRowIndex = currentIndex + this.columns;
          if (nextRowIndex < focusEls.length) {
            focusEls[nextRowIndex].focus();
          } else {
            const columnIndex = currentIndex % this.columns;
            focusEls[columnIndex].focus();
          }
        } else {
          // For regular dropdown, move to next option
          if (currentIndex === focusEls.length - 1 || currentIndex === -1) {
            firstFocusableEl.focus();
          } else {
            focusEls[currentIndex + 1].focus();
          }
        }
        setTimeout(() => this.updateActiveDescendantFromFocus(), 0);
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (this.type === 'emoji') {
          // For emoji grid layout, handle column-based navigation
          const prevRowIndex = currentIndex - this.columns;
          if (prevRowIndex >= 0) {
            focusEls[prevRowIndex].focus();
          } else {
            // Go to last item in same column
            const columnIndex = currentIndex % this.columns;
            const lastRowStart =
              Math.floor((focusEls.length - 1) / this.columns) * this.columns;
            const targetIndex = lastRowStart + columnIndex;
            const safeIndex =
              targetIndex < focusEls.length ? targetIndex : focusEls.length - 1;
            focusEls[safeIndex].focus();
          }
        } else {
          // For regular dropdown, move to previous option
          if (currentIndex === 0 || currentIndex === -1) {
            lastFocusableEl.focus();
          } else {
            focusEls[currentIndex - 1].focus();
          }
        }
        setTimeout(() => this.updateActiveDescendantFromFocus(), 0);
        break;

      case 'ArrowRight':
        if (this.type === 'emoji') {
          e.preventDefault();
          if (currentIndex === focusEls.length - 1 || currentIndex === -1) {
            firstFocusableEl.focus();
          } else {
            focusEls[currentIndex + 1].focus();
          }
          setTimeout(() => this.updateActiveDescendantFromFocus(), 0);
        }
        break;

      case 'ArrowLeft':
        if (this.type === 'emoji') {
          e.preventDefault();
          if (currentIndex === 0 || currentIndex === -1) {
            lastFocusableEl.focus();
          } else {
            focusEls[currentIndex - 1].focus();
          }
          setTimeout(() => this.updateActiveDescendantFromFocus(), 0);
        }
        break;

      case 'Home':
        e.preventDefault();
        firstFocusableEl.focus();
        setTimeout(() => this.updateActiveDescendantFromFocus(), 0);
        break;

      case 'End':
        e.preventDefault();
        lastFocusableEl.focus();
        setTimeout(() => this.updateActiveDescendantFromFocus(), 0);
        break;

      case 'Enter':
      case 'Space':
        if (
          document.activeElement.classList.contains('dropdown_option_button')
        ) {
          e.preventDefault();
          document.activeElement.click();
        }
        break;

      case 'Escape':
        e.preventDefault();
        this.handleClose();
        break;
    }
  }

  handleEscape(e) {
    if (!this.open) return;
    if (e.code === 'Escape' || e.code === 'Esc') {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.handleClose();
    }
  }

  renderEmoji(emoji) {
    const emojiObj = window.wishlist_emojis.find((e) => e.value === emoji);

    if (!emojiObj) return nothing;

    if (emojiObj.type === 'svg') {
      return html`${unsafeHTML(emojiObj.display)}`;
    }

    if (emojiObj.type === 'image') {
      return html`<img
        src="${emojiObj.display}"
        width="24"
        height="24"
        alt="${emojiObj.value}"
        loading="lazy"
      />`;
    }

    if (emojiObj.type === 'emoji') {
      return html`<span>${unsafeHTML(emojiObj.display)}</span>`;
    }
  }

  getEmojiCategories() {
    if (this.type !== 'emoji') return new Map();

    const categories = new Map();

    this.options.forEach((option) => {
      const category = option.category || 'Other';
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category).push(option);
    });

    const sortedEntries = Array.from(categories.entries()).sort(
      ([catA], [catB]) => {
        if (catA === 'Whitefox') return -1;
        if (catB === 'Whitefox') return 1;
        return catA.localeCompare(catB);
      }
    );

    return new Map(sortedEntries);
  }

  renderEmojiDropdown() {
    const categories = this.getEmojiCategories();
    let optionIndex = 0;

    return html`
      <dialog
        class="dropdown_list emoji_dropdown ${this.open ? 'is_open' : ''}"
        id="dropdown_list_box"
        role="listbox"
        @keydown=${this.handleKeyDown}
      >
        <div class="emoji_categories_container">
          ${categories.size === 0
            ? html`<div class="emoji_no_results">No emojis found</div>`
            : Array.from(categories.entries()).map(
                ([category, emojis]) => html`
                  <div class="emoji_category">
                    <h3 class="emoji_category_title">${category}</h3>
                    <ul class="emoji_category_list">
                      ${repeat(
                        emojis,
                        (option) => option.value,
                        (option) => {
                          const currentIndex = optionIndex++;
                          return html`
                            <li
                              class="dropdown_option emoji_option"
                              role="option"
                              id="option_${currentIndex}"
                              aria-selected=${this.selectedOption?.value ===
                              option.value
                                ? 'true'
                                : 'false'}
                            >
                              <button
                                type="button"
                                aria-label="${option.value}"
                                class="dropdown_option_button ${this
                                  .selectedOption?.value === option.value
                                  ? 'selected'
                                  : ''}"
                                @click=${() => this.handleSelectOption(option)}
                              >
                                ${this.renderEmoji(option.value)}
                              </button>
                            </li>
                          `;
                        }
                      )}
                    </ul>
                  </div>
                `
              )}
        </div>
      </dialog>
    `;
  }

  renderTriggerButton() {
    const displayContent = this.selectedOption
      ? this.type === 'colour'
        ? html`<div
            class="dropdown_colour_display"
            style="background: ${this.selectedOption.colour ||
            this.selectedOption.value}"
          ></div>`
        : this.type === 'emoji'
        ? this.renderEmoji(this.selectedOption.value)
        : nothing
      : nothing;

    return html`
      <button
        type="button"
        ${ref((el) => (this.triggerButton = el))}
        role="combobox"
        aria-controls="dropdown_list_box"
        aria-label="${this.ariaLabel || this.label || 'Dropdown selector'}"
        aria-expanded=${this.open ? 'true' : 'false'}
        aria-autocomplete="none"
        aria-haspopup="listbox"
        aria-activedescendant=${this.open && this.activeDescendantIndex >= 0
          ? `option_${this.activeDescendantIndex}`
          : nothing}
        @click=${this.open ? this.handleClose : this.handleOpen}
        class="dropdown_trigger_button"
      >
        ${displayContent}
      </button>
    `;
  }

  renderDropdownOptions() {
    if (this.type === 'emoji') {
      return this.renderEmojiDropdown();
    }

    return html`
      <dialog
        class="dropdown_list ${this.open ? 'is_open' : ''}"
        id="dropdown_list_box"
        role="listbox"
        @keydown=${this.handleKeyDown}
      >
        <ul class="dropdown_list_options">
          ${repeat(
            this.options,
            (option) => option.value,
            (option, index) => html`
              <li
                class="dropdown_option"
                role="option"
                id="option_${index}"
                aria-selected=${this.selectedOption?.value === option.value
                  ? 'true'
                  : 'false'}
              >
                <button
                  type="button"
                  aria-label="${option.display || option.value}"
                  class="dropdown_option_button ${this.selectedOption?.value ===
                  option.value
                    ? 'selected'
                    : ''}"
                  @click=${() => this.handleSelectOption(option)}
                >
                  ${this.type === 'colour'
                    ? html` <div
                        class="dropdown_colour_option"
                        style="background: ${option.colour || option.value}"
                      ></div>`
                    : nothing}
                  <span class="dropdown_colour_label"
                    >${option.display || option.value}</span
                  >
                </button>
              </li>
            `
          )}
        </ul>
      </dialog>
    `;
  }

  renderInput() {
    return html`
      ${this.renderTriggerButton()} ${this.renderDropdownOptions()}
      <input
        type="hidden"
        id="${ifDefined(this.id)}_hidden"
        name="${ifDefined(this.name)}"
        .value=${this.value || this.options[0].value}
      />
    `;
  }

  render() {
    return html`
      <div class="${this.tagName.toLowerCase()}">
        ${this.renderInput()} ${this.renderLabel()}
      </div>
      ${this.invalid ? this.renderValid() : nothing}
    `;
  }
}

window.customElements.get('float-dropdown') ||
  window.customElements.define('float-dropdown', FloatDropdown);
